import React, {Component, Fragment} from 'react';
import {Button, Card, CardBody, CardTitle, Row} from 'reactstrap';
import {withTranslation} from 'react-i18next';
import {Colxx, Separator} from '../../../../components/common/CustomBootstrap';
import moment from 'moment';
import {
  i18nizeDate,
  numberWithCommas,
  getStatusColor,
  mapStatus,
} from '../../../../helpers/Utils';
import {fetchIntervalTime, reactTableText} from '../../../../constants/configs';
import TransactionDetails from '../../../../containers/TransactionDetails';
import DepositsOutputForm from '../../../../containers/DepositsOutputForm';
import ModalManager from '../../../../components/modal/ModalManager';
import Breadcrumb from '../../../../containers/navs/Breadcrumb';
import ReactTableSelectFilter from '../../../../components/ReactTableSelectFilter';
import ReactTableDateFilter from '../../../../components/ReactTableDateFilter';
import ReactTableTextFilter from '../../../../components/ReactTableTextFilter';
import ReactTable from 'react-table';
import Api from '../../../../services/Api';
import {keyBy, mapValues, omit, values} from 'lodash';
import ToolTip from '../../../../components/ToolTip';
import {store} from '../../../../AppRenderer';
import {Link} from 'react-router-dom';

class List extends Component {
  constructor(props) {
    super(props);
    this.state = {
      deposits: [],
      pages: -1,
      loading: false,
      selectedStartDate: null,
      selectedEndDate: null,
      expanded: {},
    };
    this.tokens = keyBy(store.getState().general.tokens, 'symbol');
    this.renderTokenFilter = this.renderTokenFilter.bind(this);
    this.renderStatusFilter = this.renderStatusFilter.bind(this);
    this.renderDateFilter = this.renderDateFilter.bind(this);
  }

  async componentDidMount() {
    this.interval = setInterval(async () => {await this.fetchDeposits(this.table.state);}, fetchIntervalTime);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  fetchData = async state => {
    this.setState({loading: true});
    await this.fetchDeposits(state);
    this.setState({loading: false});
  };

  fetchDeposits = async state => {
    try {
      const offset = state.page * state.pageSize;
      const {data} = await Api.invoke(
        'GET',
        '/admin-ui/users-deposits',
        null,
        {
          params: {
            offset,
            limit: state.pageSize,
            ...(state.filtered.length ? omit(mapValues(keyBy(state.filtered, 'id'), 'value'), 'createdAt') : null),
            ...(this.state.selectedStartDate ? {from: moment(this.state.selectedStartDate).toISOString()} : null),
            ...(this.state.selectedEndDate ? {to: moment(this.state.selectedEndDate).toISOString()} : null),
          },
        },
      );
      this.setState({
        deposits: data.deposits,
        pages: data.totalPage,
      });
    } catch (e) {
      console.log('error: ', e);
    }
  };

  getColumns = t => [
    {
      Header: t('Transaction Id/Hash'),
      accessor: 'refId',
      width: 250,
      filterable: true,
      Filter: ReactTableTextFilter,
      Cell: props => {
        if (props.original.transaction_type === 'BANK_PAYMENT') {
          return <span>{props.original.refId}</span>;
        }
        if (props.original.transaction_id) {
          const tnxId = props.original.transaction_id;
          return (
            <div>
              <ToolTip id={`id_${props.original.id}`} text={tnxId}>
                <span>
                  {tnxId.substring(0, 8)}
                  {'...'}
                  {tnxId.substring(tnxId.length - 9, tnxId.length,)}
                </span>
              </ToolTip>
            </div>
          );
        }
        return null;
      },
    },
    {
      Header: t('First Name'),
      accessor: 'firstName',
      width: 90,
    },
    {
      Header: t('Last Name'),
      accessor: 'lastName',
      width: 120,
    },
    {
      Header: t('Email'),
      accessor: 'email',
      minWidth: 150,
    },
    {
      Header: t('Amount'),
      accessor: 'amount',
      Cell: props => <span>{numberWithCommas(Number(props.value))}</span>,
    },
    {
      Header: t('Token'),
      accessor: 'token',
      filterable: true,
      Filter: this.renderTokenFilter,
      Cell: props => <span>{this.tokens[props.value] ? this.tokens[props.value].localeName : props.value}</span>,
    },
    {
      Header: t('Status'),
      accessor: 'status',
      filterable: true,
      Filter: this.renderStatusFilter,
      Cell: props => <span className={getStatusColor(props.value)}>{mapStatus(props.value)}</span>,
    },
    {
      Header: t('Date'),
      accessor: 'timestamp',
      width: 140,
      Cell: props => <span>{i18nizeDate(props.value)}</span>,
      filterable: true,
      Filter: this.renderDateFilter,
    },
    {
      Header: t('Actions'),
      width: 60,
      Cell: props =>
        <div className='d-flex flex-row align-items-center justify-content-around'>
          <ToolTip id={`view_${props.original.userId}`} text={t('View Profile')}>
            <Link to={{
              pathname: '/dashboard/users/' + props.original.userId,
            }}>
              <i className="simple-icon-eye view-icon text-large"/>
            </Link>
          </ToolTip>
        </div>,
    },
  ];

  renderTokenFilter({filter, onChange}) {
    const {t} = this.props;
    const options = values(this.tokens).map(token => ({
      value: token.symbol, label: token.localeName,
    }));
    return <ReactTableSelectFilter
      filter={filter}
      onChange={onChange}
      options={[
        {value: '', label: t('all')},
        ...options,
      ]}
    />;
  }

  renderStatusFilter({filter, onChange}) {
    const {t} = this.props;
    const options = [
      {value: '', label: t('all')},
      {value: 'ACCEPTED', label: t('Accepted')},
      {value: 'PENDING', label: t('Pending')},
      {value: 'CONFIRMED', label: t('Confirmed')},
      {value: 'REJECTED', label: t('Rejected')},
    ];
    return <ReactTableSelectFilter
      filter={filter}
      onChange={onChange}
      options={options}
    />;
  }

  renderDateFilter({onChange}) {
    const {selectedStartDate, selectedEndDate} = this.state;
    return <ReactTableDateFilter
      onChange={onChange}
      handleDateChange={this.handleDateChange}
      selectedStartDate={selectedStartDate}
      selectedEndDate={selectedEndDate}
    />;
  }

  openOutputModal = () => {
    const {t} = this.props;
    ModalManager.open({
      isOpen: true,
      title: t('Deposits Excel Output'),
      body: <DepositsOutputForm/>,
    });
  };

  handleDateChange = (type, date) => {
    this.setState({
      [type]: date,
    });
  };

  onExpanded = (newExpanded, index) => {
    this.setState({
      expanded: {
        ...this.state.expanded,
        [index[0]]: !!newExpanded[index[0]],
      },
    });
  };

  render() {
    const {t, match} = this.props;
    const {deposits, pages, loading, expanded} = this.state;
    return (
      <Fragment>
        <Row>
          <Colxx xxs="12">
            <Breadcrumb match={match}/>
            <Separator className="mb-5"/>
          </Colxx>
        </Row>
        <Row>
          <Colxx xxs="12" className="mb-4">
            <Card className="h-100">
              <CardBody>
                <CardTitle className="d-flex flex-row justify-content-end align-items-center">
                  <Button
                    outline
                    className="d-flex align-items-center justify-content-center"
                    onClick={this.openOutputModal}>
                    <i className="icon-File-Excel text-large"/>
                    <span className="mx-1">{t('Excel output')}</span>
                  </Button>
                </CardTitle>
                <ReactTable
                  manual
                  ref={el => {this.table = el;}}
                  className="-highlight"
                  data={deposits}
                  pages={pages}
                  loading={loading}
                  sortable={false}
                  resizable={true}
                  onFetchData={this.fetchData}
                  columns={this.getColumns(t)}
                  expanded={expanded}
                  onPageChange={() => {this.setState({expanded: {}});}}
                  onExpandedChange={this.onExpanded}
                  defaultPageSize={25}
                  style={{
                    width: '100%'
                  }}
                  {...reactTableText}
                  SubComponent={row => {
                    const {original} = row;
                    return (
                      <TransactionDetails
                        showForm={false}
                        transaction={{...original, type: 'deposit'}}
                      />
                    );
                  }}
                />
              </CardBody>
            </Card>
          </Colxx>
        </Row>
      </Fragment>
    );
  }
}

export default withTranslation()(List);
